import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";

/**
 * Type-and-search dropdown for large lists (2000+ items).
 * Renders max 60 matches for performance; users refine via search.
 */
export function GISearchSelect({ value, onChange, options, placeholder = "Type to search…", testId = "gi-select" }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrap = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const matches = useMemo(() => {
    const query = (q || "").trim().toLowerCase();
    if (!query) return options.slice(0, 60);
    const starts = []; const contains = [];
    for (const opt of options) {
      const low = opt.toLowerCase();
      if (low.startsWith(query)) starts.push(opt);
      else if (low.includes(query)) contains.push(opt);
      if (starts.length + contains.length >= 200) break;
    }
    return [...starts, ...contains].slice(0, 60);
  }, [q, options]);

  const pick = (opt) => { setQ(opt); onChange?.(opt); setOpen(false); };

  const onKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi(h => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (matches[hi]) pick(matches[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="gi-select" ref={wrap} data-testid={testId}>
      <div className="gi-select-input">
        <Search size={14} />
        <input
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); setHi(0); onChange?.(e.target.value); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
          autoComplete="off"
        />
        {q && <button type="button" className="gi-select-clear" onClick={() => { setQ(""); onChange?.(""); setOpen(true); }} aria-label="Clear" data-testid={`${testId}-clear`}><X size={12} /></button>}
      </div>
      {open && matches.length > 0 && (
        <ul className="gi-select-list" data-testid={`${testId}-list`}>
          {matches.map((opt, i) => (
            <li
              key={opt}
              className={i === hi ? "hi" : ""}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
              data-testid={`${testId}-option-${i}`}
            >{opt}</li>
          ))}
          {options.length > matches.length && (
            <li className="gi-select-hint">Showing {matches.length} matches — refine your search to see more</li>
          )}
        </ul>
      )}
      {open && matches.length === 0 && (
        <ul className="gi-select-list"><li className="gi-select-hint">No matches. Check spelling or ask admin to add it.</li></ul>
      )}
    </div>
  );
}
